import { FC, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';

import { useForm } from '@tanstack/react-form';
import Countdown from 'react-countdown';

import {
  InputText,
  InputPassword,
  TextBlock,
  useToaster,
  ButtonText,
  Button,
  Title,
  Expander,
  useModal,
  Modal,
} from '../../components';
import {
  encode64,
  timerMinutesRender,
  EMAIL_SYMBOLS_REGEX,
  getCookie,
  deleteCookie,
  MFA_PENDING_COOKIE_NAME,
  parseBlockedAccountPayload,
} from '@skillarea/lib-shared';

import { TLoginTexts, TOAuthButtonsTexts } from '../../types';
import { OnboardLoginDto, RestorePasswordDto } from '@skillarea/lib-shared';
import { FormField } from '../../components';
import { SubmitButtonWrapper } from '../../components';
import { createValidator } from '@skillarea/lib-shared';

import { OAuthButtons } from './OAuthButtons';
import styles from './Widgets.module.scss';
import { EOnboardRoutes, TOnboardRoutes } from '../index';

const loginValidator = createValidator(OnboardLoginDto);
const restoreValidator = createValidator(RestorePasswordDto);

function getTwoFactorErrorMessage(result: unknown, fallback: string): string {
  if (!result || typeof result !== 'object') {
    return fallback;
  }

  if ('message' in result && typeof result.message === 'string') {
    return result.message === 'invalid_code' ? fallback : result.message;
  }

  if ('error' in result && typeof result.error === 'string') {
    return result.error === 'invalid_code' ? fallback : result.error;
  }

  return fallback;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getLoginErrorMessage(result: unknown): string | undefined {
  if (!isRecord(result)) {
    return undefined;
  }

  if (Array.isArray(result.dtoMessages)) {
    const firstDtoMessage = result.dtoMessages.find((dtoMessage) => {
      return (
        isRecord(dtoMessage) &&
        typeof dtoMessage.message === 'string' &&
        dtoMessage.message.length > 0
      );
    });

    if (
      isRecord(firstDtoMessage) &&
      typeof firstDtoMessage.message === 'string'
    ) {
      return firstDtoMessage.message;
    }
  }

  if (typeof result.message === 'string' && result.message.length > 0) {
    return result.message;
  }

  if (typeof result.error === 'string' && result.error.length > 0) {
    return result.error;
  }

  return undefined;
}

export const Login: FC<{
  mode: TOnboardRoutes;
  oauthTexts: TOAuthButtonsTexts;
  texts: TLoginTexts;
  onboardStore: any;
  onLoginSuccess?: (
    method: string,
    options?: {
      isMfaVerification?: boolean;
      mfaPendingCookieValue?: string | null;
    },
  ) => void;
}> = ({ mode, oauthTexts, texts, onboardStore, onLoginSuccess }) => {
  const { toast } = useToaster();
  const { closeModal } = useModal();
  const location = useLocation();
  const navigate = useNavigate();

  const isMfaPending =
    mode === 'login' && Boolean(getCookie(MFA_PENDING_COOKIE_NAME));

  const [resendAfter, setResendAfter] = useState<Date | null>(null);
  const [isMfaModalOpen, setIsMfaModalOpen] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [isMfaLoading, setIsMfaLoading] = useState(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState('');

  const setLoginMode = useCallback(() => {
    void navigate({ to: location.pathname, hash: EOnboardRoutes.login });
  }, [location.pathname, navigate]);

  const setRestoreMode = useCallback(() => {
    void navigate({ to: location.pathname, hash: EOnboardRoutes.restore });
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (mode !== 'login') return;
    if (isMfaPending) setIsMfaModalOpen(true);
  }, [mode, isMfaPending]);

  useEffect(() => {
    if (mode !== 'login' && loginErrorMessage) {
      setLoginErrorMessage('');
    }
  }, [loginErrorMessage, mode]);

  const handleCloseMfaModal = useCallback(() => {
    deleteCookie(MFA_PENDING_COOKIE_NAME);
    setIsMfaModalOpen(false);
    setMfaCode('');
    closeModal();
    void navigate({ to: location.pathname, hash: '', replace: true });
  }, [closeModal, location.pathname, navigate]);

  const handleVerifyMfa = useCallback(async () => {
    const code = mfaCode.trim();
    if (!code) return;

    const currentMfaPendingCookie = getCookie(MFA_PENDING_COOKIE_NAME);
    const mfaPendingCookieValue =
      typeof currentMfaPendingCookie === 'string'
        ? currentMfaPendingCookie
        : null;

    setIsMfaLoading(true);
    try {
      const result = await onboardStore.getState().verifyTotp(
        { body: { code } },
        {
          errorInterceptor: () => true,
        },
      );
      if (result?.success === false) {
        const message = isRecord(result)
          ? (result.message as unknown)
          : undefined;
        const isStaleChallengeError =
          message === 'invalid_mfa_token' || message === 'mfa_token_missing';

        if (isStaleChallengeError) {
          handleCloseMfaModal();
          toast.error(texts.staleMfaChallenge);
          return;
        }

        toast.error(
          getTwoFactorErrorMessage(result, texts.invalidTwoFactorCode),
        );
        return;
      }

      onLoginSuccess?.('email', {
        isMfaVerification: true,
        mfaPendingCookieValue,
      });
      setIsMfaModalOpen(false);
      setMfaCode('');
      closeModal();
    } catch {
      toast.error(texts.invalidTwoFactorCode);
    } finally {
      setIsMfaLoading(false);
    }
  }, [
    closeModal,
    handleCloseMfaModal,
    mfaCode,
    onboardStore,
    toast,
    onLoginSuccess,
    texts.invalidTwoFactorCode,
    texts.staleMfaChallenge,
  ]);

  const form = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
    onSubmit: async ({ value }) => {
      if (mode === 'login') {
        setLoginErrorMessage('');
        const result = await onboardStore.getState().signIn({ body: value });
        if (!result.success) {
          if (result.statusCode === 409 || result.status === 409)
            void navigate({
              to: location.pathname,
              hash: EOnboardRoutes.activate.replace(
                ':link',
                encode64(value.email),
              ),
            });
          else if (parseBlockedAccountPayload(result)) {
            void navigate({
              to: location.pathname,
              hash: '',
              replace: true,
            });
          } else {
            setLoginErrorMessage(
              getLoginErrorMessage(result) ?? 'Unable to log in',
            );
          }
        } else {
          setLoginErrorMessage('');
          if (getCookie(MFA_PENDING_COOKIE_NAME)) {
            setIsMfaModalOpen(true);
          } else {
            onLoginSuccess?.('email');
            closeModal();
          }
        }
      } else if (mode === 'restore') {
        const result = await onboardStore.getState().restorePassword({
          body: { email: value.email },
        });
        if (result.success) {
          toast.success(texts.toastSuccess);
          setResendAfter(new Date(Date.now() + result.timer));
        } else {
          // Handle errors for tanstack form
          console.error('Form submission error:', result);
        }
      }
    },
    validators: {
      onChange: mode === 'login' ? loginValidator : restoreValidator,
    },
  });

  return (
    <>
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        {mode === 'login' ? (
          <Title className={styles.header} color="accent" size="xs">
            {texts.logInTitle}
          </Title>
        ) : (
          <>
            <Title className={styles.header} color="accent" size="xs">
              {texts.restoreTitle}
            </Title>
            <TextBlock size="s" color="noteContrast" className={styles.text}>
              {texts.restoreSubTitle}
            </TextBlock>
          </>
        )}

        <OAuthButtons texts={oauthTexts} />

        <div className={styles.fields}>
          <FormField name="email" form={form}>
            <InputText
              size="xl"
              allowedPattern={EMAIL_SYMBOLS_REGEX}
              className={styles.topInput}
              placeholder={texts.emailPlaceholder}
            />
          </FormField>

          {mode === 'login' && (
            <>
              <FormField name="password" form={form}>
                <InputPassword
                  size="xl"
                  placeholder={texts.passwordPlaceholder}
                />
              </FormField>

              <div className={styles.forgot}></div>
            </>
          )}
        </div>

        {mode === 'login' && loginErrorMessage ? (
          <TextBlock color="error" size="s" data-testid="login-error-message">
            {loginErrorMessage}
          </TextBlock>
        ) : null}

        <SubmitButtonWrapper
          forceDisabled={mode === 'restore' && resendAfter ? true : undefined}
          form={form}
        >
          <Button
            className={styles.submit}
            type="submit"
            variant="filled"
            color="primary"
          >
            {mode === 'login' ? (
              texts.loginSubmit
            ) : (
              <>
                {resendAfter ? (
                  <>
                    {texts.restoreSendAgain}
                    <Countdown
                      date={resendAfter}
                      daysInHours
                      onComplete={() => setResendAfter(null)}
                      renderer={timerMinutesRender}
                    />
                  </>
                ) : (
                  texts.restoreSend
                )}
              </>
            )}
          </Button>
        </SubmitButtonWrapper>

        {mode === 'login' ? (
          <div className={styles.footer}>
            <div className={styles.bottomSingleLink}>
              <TextBlock size="s" color="noteContrast">
                {texts.noAccount}
              </TextBlock>
              <ButtonText
                color="accent"
                size="xs"
                onClick={() => {
                  void navigate({
                    to: location.pathname,
                    hash: EOnboardRoutes.register,
                  });
                }}
              >
                {texts.createNew}
              </ButtonText>
            </div>

            <Expander />

            <ButtonText size="xs" color="noteContrast" onClick={setRestoreMode}>
              {texts.forgot}
            </ButtonText>
          </div>
        ) : (
          <div className={styles.footer}>
            <ButtonText
              size="xs"
              color="noteContrast"
              className={styles.bottomSingleLink}
              onClick={setLoginMode}
            >
              {texts.back}
            </ButtonText>
          </div>
        )}
      </form>

      <Modal
        isOpen={isMfaModalOpen}
        isAutoClosing={false}
        preventOutsideClose={isMfaPending}
        onBeforeClose={handleCloseMfaModal}
        title="Two-factor authentication"
      >
        <form
          className={styles.mfaModal}
          data-testid="mfa-login-modal"
          onSubmit={(e) => {
            e.preventDefault();
            void handleVerifyMfa();
          }}
        >
          <TextBlock color="contrast" weight="600">
            Enter your code
          </TextBlock>
          <TextBlock className={styles.mfaHint} color="noteContrast" size="s">
            Use the code from Google Authenticator to finish signing in.
          </TextBlock>
          <InputText
            size="xl"
            placeholder="Authenticator code"
            value={mfaCode}
            onChange={(value) => setMfaCode(value)}
            data-testid="mfa-login-code-input"
          />
          <div className={styles.mfaActions}>
            <Button
              type="submit"
              variant="filled"
              color="primary"
              disabled={!mfaCode || isMfaLoading}
              data-testid="mfa-login-submit"
            >
              Verify
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
};
